import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import VolumeCalculator from "../components/VolumeCalculator";
import FloatingContact from "../components/FloatingContact";
import SEOHead from "../components/SEOHead";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Sparkles, TrendingDown, Calculator, Coins } from "lucide-react";
import { useSiteT } from "../hooks/useSiteT";

const SITE_URL = "https://adrise-agency.com";

export default function CalculatorPage() {
    const { t } = useSiteT();
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        name: "Adrise Adspend Fee Calculator",
        url: `${SITE_URL}/calculator`,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        description:
            "Free interactive calculator to find your Adrise adspend fee tier and monthly cashback eligibility based on monthly ad spend and vertical.",
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    };

    return (
        <main
            data-testid="calculator-page"
            className="bg-background text-foreground min-h-screen"
        >
            <SEOHead
                title="How much will I save with Adrise? — Adspend Fee Calculator"
                description="Free interactive calculator. Drop your monthly ad spend, pick your vertical, and see exactly what you'd pay with Adrise Credit Line accounts vs traditional. Cashback above $1M."
                canonical={`${SITE_URL}/calculator`}
                keywords={[
                    "ad spend calculator",
                    "agency ad fee calculator",
                    "credit line cost calculator",
                    "ad spend cashback",
                    "media buying cost",
                ]}
                jsonLd={jsonLd}
            />

            <Navbar />

            <section className="relative pt-32 md:pt-40 pb-12 overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
                <div className="absolute inset-0 noise pointer-events-none" />
                <div className="container-skala relative">
                    <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 mb-6">
                        <Calculator className="w-3.5 h-3.5 text-primary" />
                        <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-primary">
                            {t("calc.badge")}
                        </span>
                    </div>
                    <h1 className="font-display font-black text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] max-w-4xl">
                        {t("calc.h1a")}{" "}
                        <span className="text-gradient-primary">{t("calc.h1b")}</span>{" "}
                        {t("calc.h1c")}
                    </h1>
                    <p className="text-base md:text-lg text-muted-foreground mt-6 max-w-2xl">
                        {t("calc.sub")}
                    </p>
                </div>
            </section>

            <section className="pb-16">
                <div className="container-skala">
                    <VolumeCalculator />
                </div>
            </section>

            {/* Tier explainer */}
            <section className="py-16 border-y border-white/5 bg-white/[0.01]">
                <div className="container-skala">
                    <div className="grid md:grid-cols-3 gap-4">
                        <div className="bg-card border border-white/[0.08] rounded-2xl p-6">
                            <TrendingDown className="w-5 h-5 text-primary mb-3" />
                            <h3 className="font-display font-semibold text-lg mb-1">
                                {t("calc.c1t")}
                            </h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                {t("calc.c1")}
                            </p>
                        </div>
                        <div className="bg-card border border-[#2FA57C]/30 rounded-2xl p-6">
                            <Coins className="w-5 h-5 text-[#2FA57C] mb-3" />
                            <h3 className="font-display font-semibold text-lg mb-1">
                                {t("calc.c2t")}
                            </h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                {t("calc.c2")}
                            </p>
                        </div>
                        <div className="bg-card border border-white/[0.08] rounded-2xl p-6">
                            <Sparkles className="w-5 h-5 text-primary mb-3" />
                            <h3 className="font-display font-semibold text-lg mb-1">
                                {t("calc.c3t")}
                            </h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                {t("calc.c3")}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 md:py-20">
                <div className="container-skala text-center">
                    <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-4">
                        {t("calc.ctaTitle")}
                    </h2>
                    <p className="text-muted-foreground max-w-xl mx-auto mb-7">
                        {t("calc.ctaSub")}
                    </p>
                    <div className="flex flex-wrap justify-center gap-3">
                        <Link to="/signup" data-testid="calc-cta-apply">
                            <Button
                                size="lg"
                                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-12 px-7"
                            >
                                {t("calc.apply")}
                            </Button>
                        </Link>
                        <Link to="/#pricing" data-testid="calc-cta-pricing">
                            <Button
                                size="lg"
                                variant="outline"
                                className="border-white/15 bg-white/[0.02] h-12 px-7"
                            >
                                {t("calc.pricing")} →
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            <FloatingContact />
            <Footer />
        </main>
    );
}
